import { IAddress } from '@@types/address';
import { TOrderCreate } from '@@types/order';
import MarketCouponEventDrawer from '@components/event/market-coupon-event.drawer';
import queryKeys from '@constants/query-keys';
import ROUTER from '@constants/router';
import { useCreateOrder } from '@containers/checkout/queries';
import { cartSelector, initCart } from '@stores/cart';
import { eventSelector } from '@stores/event';
import { setMyInfo } from '@stores/myInfo';
import {
  initOrderInfo,
  orderSelector,
  storeOrderAddressInfo,
  storeOrderInfo,
  TOrderInitialState,
} from '@stores/order';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useLoader from './useLoader';
import usePopup from './usePopup';

export default function useOrder() {
  const dispatch = useDispatch();
  const order = useSelector(orderSelector);
  const cart = useSelector(cartSelector);
  const { discountConstant, discountRate } = useSelector(eventSelector);
  const { t } = useTranslation('store');
  const { closePopup, openAlert, openPopup } = usePopup();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { progressLoader, stopLoader } = useLoader();
  const { mutate, mutateAsync, isLoading } = useCreateOrder({
    onMutate: () => {
      progressLoader();
    },
    onSuccess: async ({ data }) => {
      if (data.code !== 200) throw data;
      queryClient.invalidateQueries([...queryKeys.USER.INFO]);
      queryClient.invalidateQueries([...queryKeys.USER.MONEY]);
      await closePopup('cart');

      if (!!data.data && !!data.data._id) {
        queryClient.setQueryData(
          [...queryKeys.ORDER.COMPLETED, data.data._id],
          () => data.data,
        );

        await router.push(
          `${
            data.data.step === '접수'
              ? `${ROUTER.depositInfo(data.data._id)}`
              : `${ROUTER.order}?type=${data.data._id}`
          }`,
        );
        localStorage.removeItem('cart');

        dispatch(initCart({}));
        openPopup({
          isOpen: true,
          type: 'drawer',
          popupOptions: { placement: 'bottom', size: 'lg' },
          Component: MarketCouponEventDrawer,
          props: {},
        });
      }
    },
    onError: (e: any) => {
      console.log(e);
      if (typeof e === 'object') {
        if (e?.code && e.code === 429)
          openAlert({
            header: 'Error',
            description: '' + t(e.message),
            isCancelButtonShow: false,
            confirmButtonText: '' + t('common:ok'),
          });
        else
          openAlert({
            header: 'Error',
            description: '' + t(e.message),
            isCancelButtonShow: false,
            confirmButtonText: '' + t('common:ok'),
          });
      }
    },
    onSettled: () => {
      stopLoader();
    },
  });
  const productAmount = () => {
    return (
      cart?.menus?.reduce((acc, menu) => {
        let _price = 0;
        _price += menu.originalPrice;
        if (menu.options.length > 0) {
          menu.options.forEach((option) => {
            _price += option.marginPrice;
          });
        }
        acc += _price * menu.quantity;
        return acc;
      }, 0) || 0
    );
  };
  const hotdealCount =
    cart?.menus?.reduce((acc, menu) => {
      if (menu.isHotdeal) {
        acc += 1 * menu.quantity;
      }
      return acc;
    }, 0) || 0;
  const promotionDiscount =
    cart?.menus?.reduce((acc, menu) => {
      const sum = menu.originalPrice - menu.finalPrice;
      acc += sum * menu.quantity;
      return acc;
    }, 0) || 0;

  const pa = productAmount();

  const df =
    (cart?.store?.adjusted_delivery_fee || 0) + (cart?.store?.addrResFee || 0);
  const hotdealAmount = cart.store
    ? cart.store.isAllHotdeal === 2
      ? 1000
      : cart.store.isAllHotdeal === 1
      ? hotdealCount * 1000
      : 0
    : 0;

  const da =
    (order.useCash || 0) +
    (order.usePoint || 0) +
    hotdealAmount +
    promotionDiscount +
    (discountConstant || 0) +
    (order.coupon?.amount || 0);
  const depositAmount = pa + df - da;
  const finalAmount =
    (pa || 0) +
    df -
    hotdealAmount -
    promotionDiscount -
    (discountConstant || 0);

  const handleStoreOrderOption = (value: TOrderInitialState) => {
    dispatch(storeOrderInfo(value));
  };
  const handleInitOrderOption = (key?: keyof TOrderCreate) => {
    dispatch(initOrderInfo({ key }));
  };

  const handleStoreOrderAddress = (value: Partial<IAddress>) => {
    dispatch(storeOrderAddressInfo(value));
  };

  const validateOrder = (tp: number) => {
    const {
      address,
      cart,
      messageToDelivery,
      userPhone,
      marginProductAmount,
      addDeliveryFeeMethod,
    } = order;

    if (!cart || !cart.store) {
      return {
        state: 'fail',
        message: '' + t('empty_cart_message'),
        section: 'cart',
      };
    }
    if (!address || !address.detail) {
      return {
        state: 'fail',
        message: '' + t('enter_detailed_address'),
        section: 'address',
      };
    }
    if (!userPhone) {
      return {
        state: 'fail',
        message: '' + t('enter_phone_number'),
        section: 'phone',
      };
    }

    if (!messageToDelivery) {
      return {
        state: 'fail',
        message: '' + t('messageRequest:requestDelivery'),
        section: 'message',
      };
    }

    if (!addDeliveryFeeMethod) {
      return {
        state: 'fail',
        message: '' + t('messageRequest:additionalDeliveryDisable'),
        section: 'addDeliveryFeeMethod',
      };
    }

    if (marginProductAmount && marginProductAmount < 0) {
      return {
        state: 'fail',
        message:
          '일시적인 오류가 발생했습니다. 새로고침 후 다시 시도해 주세요.',
        section: 'point',
      };
    }

    return {
      state: 'success',
      data: order,
    };
  };

  const handleSubmit = async (values: TOrderCreate) => {
    const recommender = localStorage.getItem('recommender');
    if (recommender) localStorage.removeItem('recommender');
    console.log(values);
    await mutateAsync({
      ...values,
      address: {
        ...values.address,
        detail: `${values.address.detail}${
          values.address.firstFloorPassword
            ? `/ 1층 비번 ${values.address.firstFloorPassword}`
            : ''
        }`,
      },
      marginProductAmount: pa,
      discountAmount: da,
      marginDeliveryFee: df,
      depositAmount,
      promotion: {
        discountRate,
        discountConstant,
      },
      promotionDiscount: {
        rateDiscount: promotionDiscount,
        constantDiscount: discountConstant || 0,
      },
      hotdealCount,
      hotdealAmount,
      recommender: recommender || undefined,
    });
  };

  return {
    updateOrderInfo: handleStoreOrderOption,
    initOrderOption: handleInitOrderOption,
    updateOrderAddressInfo: handleStoreOrderAddress,
    validateOrder,
    order,
    onSubmit: handleSubmit,
    productAmount: pa,
    discountAmount: da,
    deliveryFee: df,
    totalPrice: depositAmount,
    hotdealCount,
    promotionDiscount,
    finalAmount,
    hotdealAmount,
    promotionConstant: discountConstant || 0,
    isOrdering: isLoading,
  };
}
